declare var $: any;

import { v5 as uuidv5, NIL as NIL_UUID } from 'uuid';

import { environment } from 'src/environments/environment';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { StartRule, TermUnit } from 'src/app/models/enums';
import { EnvironmentService } from '../environment.service';

const months = [  'January', 'February', 'March', 'April', 'May', 'June',   'July', 'August', 'September', 'October', 'November', 'December'];
  
const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const Utils = {
  formatDateInTimezone,
  formatTimeInTimezone,
  getTodayDateStringInTimezone,
  getSessionCalendarDateString,
  isSessionStartInPastInTimezone,
  isSessionStartInFutureInTimezone,
  getLocalDateString,
  combineSessionDateAndTime,
  combineSessionStartAndEnd,
  formatSessionDisplayParts,
  getShortTimeZoneName,
  isValidIanaTimeZone,
  resolveSessionTimeZone,
  resolveSessionTimeZoneFromLocations,
  toIcsDateTimeParts,
  getTimestampFromDateTime,
  getTimestampFromDateTimeUTC,
  // getLocalDateTime,
  formatDate,
  getStudioId,
  createSlug,
  reverseSlug,
  makeElementFloating,
  convertToQueryString,
  stringToGUID,
  hasStepCompleted,
  getEnumNameFromValue,
  newLineToBr,
  isNullOrEmpty,
  nextDate,
  startDate,
  getPurchaseDateText,
  getPreviousUnit,
  formatCurrency,
  toTitleCase,
  formatTimestamp,
  formatTimestampUTC,
  getOrdinal,
  getOrdinalString,
  parseDescriptionWithLinks,
  /** Internal pause SKU — not sold on the public purchase/pricing page (matches facility API). */
  isPauseSubscriptionMenuPack(menuPack: { Name?: string }): boolean {
    return (menuPack.Name ?? '').toLowerCase().includes('pause subscription');
  },
  isPauseSubscriptionCategory(category: { Name?: string; Slug?: string }): boolean {
    const name = (category.Name ?? '').toLowerCase();
    const slug = (category.Slug ?? '').toLowerCase();
    return name.includes('pause subscription') || slug.includes('pause_subscription');
  },
};

/** YYYY-MM-DD for an instant in the given IANA timezone (e.g. America/New_York). */
export function formatDateInTimezone(date: Date | string, timeZone: string): string {
  const d = typeof date === 'string' ? new Date(date) : new Date(date);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** Today's calendar date in the given IANA timezone. */
export function getTodayDateStringInTimezone(timeZone: string): string {
  return formatDateInTimezone(new Date(), timeZone);
}

/**
 * Facility calendar date (YYYY-MM-DD) from API session StartDate.
 * Does not shift the day when the browser/system timezone differs from the location.
 */
export function getSessionCalendarDateString(
  date: Date | string | null | undefined
): string {
  if (date == null) {
    return '';
  }
  if (typeof date === 'string') {
    const match = date.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
      return match[1];
    }
  }
  const d = date instanceof Date ? date : new Date(String(date));
  if (isNaN(d.getTime())) {
    return '';
  }
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface WallClockParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function getWallClockPartsInTimezone(instant: Date, timeZone: string): WallClockParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(instant);
  const get = (type: string): number =>
    parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

/** HH:mm:ss for an instant in the given IANA timezone. */
export function formatTimeInTimezone(instant: Date, timeZone: string): string {
  const p = getWallClockPartsInTimezone(instant, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
}

function normalizeWallClockTime(time: string): string {
  const segments = time.split(':').map((s) => parseInt(s, 10));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(segments[0] ?? 0)}:${pad(segments[1] ?? 0)}:${pad(segments[2] ?? 0)}`;
}

/**
 * True when session start (calendar date + wall time) is strictly before [reference] in [timeZone].
 * Uses location/facility timezone only — not the browser's system timezone.
 */
export function isSessionStartInPastInTimezone(
  sessionStartDate: Date | string,
  sessionStartTime: string,
  timeZone: string,
  reference: Date = new Date()
): boolean {
  const sessionDate = getSessionCalendarDateString(sessionStartDate);
  const refDate = formatDateInTimezone(reference, timeZone);
  if (sessionDate !== refDate) {
    return sessionDate < refDate;
  }
  return normalizeWallClockTime(sessionStartTime) < formatTimeInTimezone(reference, timeZone);
}

/** True when session start is now or later in [timeZone] (still bookable). */
export function isSessionStartInFutureInTimezone(
  sessionStartDate: Date | string,
  sessionStartTime: string,
  timeZone: string,
  reference: Date = new Date()
): boolean {
  return !isSessionStartInPastInTimezone(
    sessionStartDate,
    sessionStartTime,
    timeZone,
    reference
  );
}

/** YYYY-MM-DD in browser local time, or in [timeZone] when provided. */
export function getLocalDateString(date: Date | string, timeZone?: string): string {
  if (timeZone) {
    return formatDateInTimezone(date, timeZone);
  }
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Offset (ms) of [timeZone] from UTC at [instant], such that:
 * utcMs = wallClockAsUtcMs - getTimeZoneOffsetMs(instant, timeZone)
 */
function getTimeZoneOffsetMs(instant: Date, timeZone: string): number {
  const p = getWallClockPartsInTimezone(instant, timeZone);
  // Intl may report hour as 24 at midnight in some engines
  const hour = p.hour === 24 ? 0 : p.hour;
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, hour, p.minute, p.second);
  return asUtc - instant.getTime();
}

function wallClockMatchesInTimezone(
  instant: Date,
  timeZone: string,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number
): boolean {
  const p = getWallClockPartsInTimezone(instant, timeZone);
  const h = p.hour === 24 ? 0 : p.hour;
  return (
    p.year === year &&
    p.month === month &&
    p.day === day &&
    h === hour &&
    p.minute === minute &&
    p.second === second
  );
}

/** Resolve wall-clock in [timeZone] to a UTC instant (two-pass offset). */
function wallClockToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string
): number {
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  let utcMs = wallAsUtc - getTimeZoneOffsetMs(new Date(wallAsUtc), timeZone);
  utcMs = wallAsUtc - getTimeZoneOffsetMs(new Date(utcMs), timeZone);
  return utcMs;
}

export function isValidIanaTimeZone(timeZone: string): boolean {
  try {
    Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/**
 * First valid IANA timezone from candidates, else America/New_York.
 * Never falls back to the visitor browser zone (that skips facility→local conversion).
 */
export function resolveSessionTimeZone(
  ...candidates: Array<string | null | undefined>
): string {
  for (const tz of candidates) {
    if (tz && isValidIanaTimeZone(tz)) {
      return tz;
    }
  }
  return 'America/New_York';
}

/** Prefer the session location's TimeZone, then facility default. */
export function resolveSessionTimeZoneFromLocations(
  locationId: number | null | undefined,
  locations: Array<{ Id: number; TimeZone?: string }> | null | undefined,
  facilityTimeZone?: string | null
): string {
  const locationTz =
    locationId != null
      ? locations?.find((loc) => loc.Id === locationId)?.TimeZone
      : undefined;
  return resolveSessionTimeZone(locationTz, facilityTimeZone);
}

/**
 * Interpret facility/location wall-clock (StartDate + StartTime) as an absolute instant.
 * When [timeZone] is omitted, falls back to treating the wall clock as the browser's local time.
 *
 * DST gap policy (spring-forward): if the requested local time does not exist, advance to the
 * first valid local time on/after that wall clock (e.g. 02:30 → 03:00 America/New_York).
 * DST overlap (fall-back): the two-pass resolver picks one valid occurrence; round-trip is verified.
 */
export function combineSessionDateAndTime(
  date: Date | string,
  time: string,
  timeZone?: string
): Date {
  const dateStr = getSessionCalendarDateString(date);
  if (!dateStr) {
    return new Date(NaN);
  }
  const normalizedTime = normalizeWallClockTime(
    time?.length === 5 ? `${time}:00` : (time ?? '00:00:00')
  );
  const [y, m, d] = dateStr.split('-').map((v) => parseInt(v, 10));
  const [hh, mm, ss] = normalizedTime.split(':').map((v) => parseInt(v, 10));

  if (!timeZone) {
    return new Date(y, m - 1, d, hh, mm, ss);
  }

  let utcMs = wallClockToUtcMs(y, m, d, hh, mm, ss, timeZone);
  let result = new Date(utcMs);

  if (wallClockMatchesInTimezone(result, timeZone, y, m, d, hh, mm, ss)) {
    return result;
  }

  // DST spring-forward gap: walk forward minute-by-minute until round-trip succeeds (max 3h).
  for (let addMin = 1; addMin <= 180; addMin++) {
    const probe = new Date(Date.UTC(y, m - 1, d, hh, mm + addMin, ss));
    const py = probe.getUTCFullYear();
    const pm = probe.getUTCMonth() + 1;
    const pd = probe.getUTCDate();
    const ph = probe.getUTCHours();
    const pmin = probe.getUTCMinutes();
    const ps = probe.getUTCSeconds();
    utcMs = wallClockToUtcMs(py, pm, pd, ph, pmin, ps, timeZone);
    result = new Date(utcMs);
    if (wallClockMatchesInTimezone(result, timeZone, py, pm, pd, ph, pmin, ps)) {
      return result;
    }
  }

  return result;
}

/** Absolute timestamp for a session wall-clock in [timeZone] (facility/location IANA). */
export function getTimestampFromDateTime(
  date: Date | string,
  time: string,
  timeZone?: string
): number {
  return combineSessionDateAndTime(date, time, timeZone).getTime();
}

/** Add whole civil days to a YYYY-MM-DD string (UTC calendar arithmetic). */
function addCalendarDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map((v) => parseInt(v, 10));
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * Resolve start/end instants for a session.
 * When EndTime is earlier than or equal to StartTime on the same civil date (cross-midnight),
 * advance the end to the next calendar day.
 */
export function combineSessionStartAndEnd(
  startDate: Date | string,
  startTime: string,
  endTime: string,
  timeZone?: string
): { start: Date; end: Date } {
  const start = combineSessionDateAndTime(startDate, startTime, timeZone);
  let end = combineSessionDateAndTime(startDate, endTime, timeZone);
  if (!(end.getTime() > start.getTime())) {
    const nextDay = addCalendarDays(getSessionCalendarDateString(startDate), 1);
    end = combineSessionDateAndTime(nextDay, endTime, timeZone);
  }
  return { start, end };
}

/** ICS DateArray parts: [year, month 1-12, day, hour, minute] in UTC. */
export type IcsDateTimeParts = [number, number, number, number, number];

/**
 * Convert an absolute Date to ics DateArray parts in UTC (month 1-12).
 * Pair with EventAttributes startInputType/endInputType: 'utc' so DTSTART/DTEND
 * keep the booked instant when imported in another timezone.
 */
export function toIcsDateTimeParts(instant: Date): IcsDateTimeParts {
  return [
    instant.getUTCFullYear(),
    instant.getUTCMonth() + 1,
    instant.getUTCDate(),
    instant.getUTCHours(),
    instant.getUTCMinutes(),
  ];
}

/**
 * Short timezone label for display (e.g. EDT, CDT, EEST).
 * Prefers Intl alphabetic abbreviations; otherwise derives initials from the long name.
 */
export function getShortTimeZoneName(
  instant: Date = new Date(),
  timeZone?: string
): string {
  const withZone = (timeZoneName: 'short' | 'long'): string => {
    try {
      const opts: Intl.DateTimeFormatOptions = {
        timeZoneName,
        hour: 'numeric',
        ...(timeZone ? { timeZone } : {}),
      };
      return (
        new Intl.DateTimeFormat('en-US', opts)
          .formatToParts(instant)
          .find((p) => p.type === 'timeZoneName')?.value ?? ''
      );
    } catch {
      return '';
    }
  };

  const short = withZone('short');
  if (/^[A-Za-z]{2,5}$/.test(short)) {
    return short.toUpperCase();
  }

  const long = withZone('long');
  if (long) {
    // "Eastern Daylight Time" → EDT; "Eastern European Summer Time" → EEST
    const abbr = long
      .split(/\s+/)
      .map((w) => w.charAt(0))
      .join('')
      .toUpperCase();
    if (abbr.length >= 2 && abbr.length <= 5) {
      return abbr;
    }
  }

  return short; // e.g. "GMT+3" when no better label exists
}

/**
 * Format session start/end for display in the visitor's local timezone.
 * Interprets StartDate+StartTime as facility/location wall-clock, then formats
 * both the calendar date and the clock times in the browser's local timezone.
 */
export function formatSessionDisplayParts(
  startDate: Date | string,
  startTime: string,
  endTime: string,
  facilityTimeZone: string
): {
  dayOfWeek: string;
  month: string;
  dayOfMonth: string;
  /** e.g. "6:00" (AM/PM stripped; matches legacy class-details header). */
  startTime: string;
  /** e.g. "7:00 PM" */
  endTime: string;
  /** e.g. "6:00 PM" — full local start for meta/share copy. */
  startTimeFull: string;
  /** e.g. "EDT" / "EEST" — visitor-local zone abbreviation. */
  timeZoneAbbr: string;
} {
  const { start: startInstant, end: endInstant } = combineSessionStartAndEnd(
    startDate,
    startTime,
    endTime,
    facilityTimeZone
  );
  const timeZoneAbbr = getShortTimeZoneName(startInstant);

  const dateParts = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).formatToParts(startInstant);
  const part = (type: string) => dateParts.find((p) => p.type === type)?.value ?? '';

  const timeOpts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric' };
  const startTimeFull = startInstant.toLocaleTimeString('en-US', timeOpts);
  const endTimeFull = endInstant.toLocaleTimeString('en-US', timeOpts);

  return {
    dayOfWeek: part('weekday'),
    month: part('month'),
    dayOfMonth: part('day'),
    startTime: startTimeFull.replace(' AM', '').replace(' PM', ''),
    endTime: endTimeFull,
    startTimeFull,
    timeZoneAbbr,
  };
}

export function getTimestampFromDateTimeUTC(date: Date, time: string) : number
{
  var datetimeString: string = date.toISOString().substring(0, 10)+ "T" + time;
  date = new Date(Date.parse(datetimeString));
  let date_utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(),
    date.getUTCDate(), date.getUTCHours(),
    date.getUTCMinutes(), date.getUTCSeconds())); 
  return date_utc.getTime();
}

// export function getLocalDateTime(date: Date): Date {
//   const localDateString = date.toLocaleString('en-US', { timeZone: environment.facility.timezone });
//   return new Date(localDateString);
// }

export function formatDate(date: Date, pattern = 'yyyy-MM-dd HH:mm:ss Z'): string {
  const replacements: { [key: string]: string } = {
    yyyy: date.getFullYear().toString(),
    yy: (date.getFullYear() % 100).toString().padStart(2, '0'),
    MMMM: months[date.getMonth()],
    MMM: months[date.getMonth()].substring(0, 3),
    MM: (date.getMonth() + 1).toString().padStart(2, '0'),
    M: (date.getMonth() + 1).toString(),
    dddd: weekdays[date.getDay()],
    ddd: weekdays[date.getDay()].substring(0, 3),
    ddy: date.getDate().toString().padStart(2, '0'),
    dy: date.getDate().toString(),
    dd: date.getDate().toString().padStart(2, '0'),
    d: date.getDate().toString(),
    HH: date.getHours().toString().padStart(2, '0'),
    H: date.getHours().toString(),
    hh: (date.getHours() % 12 || date.getHours()).toString().padStart(2, '0'),
    h: (date.getHours() % 12 || date.getHours()).toString(),
    mm: date.getMinutes().toString().padStart(2, '0'),
    m: date.getMinutes().toString(),
    ss: date.getSeconds().toString().padStart(2, '0'),
    s: date.getSeconds().toString(),
    Z: date.toTimeString().replace(/.+GMT([+-]\d+).+/, '$1'),
    x: date.getHours() < 12 ? 'am' : 'pm',
    X: date.getHours() < 12 ? 'AM' : 'PM',
  };
  const suffixes: { [key: string]: string } = {
    '1': 'st',
    '2': 'nd',
    '3': 'rd',
    '21': 'st',
    '22': 'nd',
    '23': 'rd',
    '31': 'st',
  };
  const formattedString = pattern.replace(/(\w+)(\W)?/g, (_, key, separator) => {
    if (key in replacements) {
      let replacedValue = replacements[key];
      if (key === 'd') {
        const day = date.getDate().toString();
        replacedValue += '';
      }
      if (key === 'dd') {
        const day = date.getDate().toString().padStart(2, '0');
        replacedValue += '';
      }
      if (key === 'dy') {
        const day = date.getDate().toString();
        replacedValue += suffixes[day] || 'th';
      }
      if (key === 'ddy') {
        const day = date.getDate().toString().padStart(2, '0');
        replacedValue += suffixes[day] || 'th';
      }
      if (key === 'm') {
        const month = (date.getMonth() + 1).toString();
        replacedValue = replacedValue.replace(key, month);
      }
      if (key === 'mm') {
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        replacedValue = replacedValue.replace(key, month);
      }
      if (key === 'x' || key === 'X') {
        const replacement = key === 'x' ? replacements['x'] : replacements['X'];
        replacedValue = replacedValue.replace(key, replacement);
      }
      return replacedValue + (separator || '');
    }
    return key;
  });
  return formattedString;
}

export function getStudioId(studioSlug: string): number {
  switch (studioSlug.toLowerCase()) {
    case 'fhitting-room': return 1;
    case 'humming-puppy': return 2;
    case 'mhrc': return 3;
    case 'physique-57': return 4;
    case 'turf-room': return 5;
    default:  return 0;
  }
}

export function createSlug(name: string) {
  // Convert name to lowercase and replace spaces with hyphens
  let slug = name.toLowerCase().replace(/\s+/g, '-');

  // Remove any special characters
  slug = slug.replace(/[^\w\-]/g, '');

  // Replace any remaining special characters with a hyphen
  slug = slug.replace(/\-+/g, '-');

  // Trim any leading or trailing hyphens
  slug = slug.replace(/^\-+|\-+$/g, '');

  return slug;
}

export function reverseSlug(slug: string) {
  // Replace any duplicate hyphens with a space
  let name = slug.replace(/\-{2,}/g, ' ');

  // Replace any remaining hyphens with a space
  name = name.replace(/\-/g, ' ');

  // Capitalize the first letter of each word
  name = name.replace(/(?:^|\s)\S/g, function(a) { return a.toUpperCase(); });

  return name;
}

export function makeElementFloating(referenceElem: string, elem: string)
{
  var windowTop = $(window).scrollTop();
  var windowBottom = windowTop + $(window).height();
  var divTop = $(referenceElem).offset().top;
  var divBottom = divTop + $(referenceElem).height();
  if ((divTop <= windowBottom) && (divBottom >= windowTop)) {
    $(elem).css('position', 'relative');
  } else {
    $(elem).css('position', 'fixed');
  }
}

export function stringToGUID(input: string): string {
  // Generate a new GUID using the input string as the namespace
  const guid = uuidv5(input, NIL_UUID);

  return guid;
}

export function hasStepCompleted(input: string, token: string, delimiter: string = "|"): boolean {
  const explodedArray = input.split(delimiter);
  return explodedArray.includes(token);
}

export function getEnumNameFromValue(enumType: any, value: number): string {
    return enumType[value] || '';
}

export function getPurchaseDateText(startRule: StartRule, startOffset: number, openingDate: Date | null): string {
  var purchaseDateText = '';
  switch (startRule) {
    case StartRule.Immediate:
      purchaseDateText = 'immediately';
      break;
    case StartRule.ProratedFirstCycle:
      purchaseDateText = 'immediately with first cycle (prorated)';
      break;
    case StartRule.StartOfOpeningDate:
      if (openingDate && openingDate instanceof Date) {
        const now = new Date();
        if (openingDate < now) {
          purchaseDateText = 'immediately';
          break;
        } else {
          purchaseDateText = `opening`;// date (${formatDate(openingDate, 'MMM dd, yyyy')})`;
          break;
        }
      }
      purchaseDateText = 'opening';
      break;
    case StartRule.StartOnFirstOfMonth:
      purchaseDateText = 'first of the month';
      break;
    case StartRule.DefferedByDays: {
      let now = new Date();
      let afterText = `after ${startOffset} day${startOffset > 1 ? 's' : ''}`;
      if (openingDate && openingDate instanceof Date && openingDate > now) {
        purchaseDateText = `${afterText} of opening`;
        break;
      } else {
        purchaseDateText = `${afterText} of purchase`;
        break;
      }
    }
    case StartRule.DefferedByMonths:
    case StartRule.ProratedDefferedByMonths: {
      let now = new Date();
      let afterText = `after ${startOffset} month${startOffset > 1 ? 's' : ''}`;
      if (openingDate && openingDate instanceof Date && openingDate > now) {
        purchaseDateText = `${afterText} of opening`;
        break;
      } else {
        purchaseDateText = `${afterText} of purchase`;
        break;
      }
    }
    case StartRule.Unset:
      purchaseDateText = '';
      break;
    default:
      purchaseDateText = 'Start date rule not specified';
      break;
  }
  return purchaseDateText;
}

export function newLineToBr(text: string | null | undefined, sanitizer: DomSanitizer): SafeHtml {
  if (!text) {
    return sanitizer.bypassSecurityTrustHtml('');
  }
  const html = text.replace(/\n/g, '<br/>');
  return sanitizer.bypassSecurityTrustHtml(html);
}

export function isNullOrEmpty(inputString: string | null | undefined): boolean {
  return inputString == null || inputString == undefined || inputString.trim() == '';
}

export function nextDate(startDate: Date, duration: number, unit: TermUnit): Date {
  let finalDate = new Date(startDate);

  switch (unit) {
      case TermUnit.Day:
          finalDate.setDate(startDate.getDate() + duration);
          break;
      case TermUnit.Week:
          finalDate.setDate(startDate.getDate() + duration * 7);
          break;
      case TermUnit.Month:
          finalDate.setMonth(startDate.getMonth() + duration);
          const lastDayOfMonth = new Date(finalDate.getFullYear(), finalDate.getMonth() + 1, 0).getDate();
          finalDate.setDate(Math.min(lastDayOfMonth, startDate.getDate()));
          break;
      case TermUnit.Year:
          finalDate.setFullYear(startDate.getFullYear() + duration);
          break;
      case TermUnit.Unset:
          break;
      default:
          finalDate.setMonth(startDate.getMonth() + duration);
          break;
  }
  return finalDate;
}

export function startDate(startDate: Date, duration: number, unit: StartRule): Date {
  let finalDate = new Date(startDate);

  switch (unit) {
      case StartRule.Immediate:
          finalDate.setDate(startDate.getDate() + duration);
          break;
      case StartRule.ProratedFirstCycle:
          finalDate.setDate(startDate.getDate() + duration * 7);
          break;
      case StartRule.StartOfOpeningDate:
          finalDate.setMonth(startDate.getMonth() + duration);
          const lastDayOfMonth = new Date(finalDate.getFullYear(), finalDate.getMonth() + 1, 0).getDate();
          finalDate.setDate(Math.min(lastDayOfMonth, startDate.getDate()));
          break;
      case StartRule.StartOnFirstOfMonth:
          finalDate.setFullYear(startDate.getFullYear() + duration);
          break;
      case StartRule.DefferedByDays:
          finalDate.setFullYear(startDate.getFullYear() + duration);
          break;
      case StartRule.DefferedByMonths:
          finalDate.setFullYear(startDate.getFullYear() + duration);
          break;
      case StartRule.Unset:
          break;
      default:
          finalDate.setMonth(startDate.getMonth() + duration);
          break;
  }
  return finalDate;
}

export function getPreviousUnit(unit: TermUnit): TermUnit {
  if (unit === TermUnit.Day) return TermUnit.Unset; // Since there's no enum before Day
  return unit - 1;
}

export function convertToQueryString(obj: any, parentKey?: string): string {
  const queryStringArray: string[] = [];

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];

      const newKey = parentKey ? `${parentKey}.${key}` : key;  // Adjust newKey format

      if (value instanceof Array) {
        for (let i = 0; i < value.length; i++) {
          const arrayKey = `${newKey}[${i}]`;
          const arrayValue = value[i];
          queryStringArray.push(convertToQueryString(arrayValue, arrayKey));
        }
      } else if (typeof value === 'object' && value !== null) {
        queryStringArray.push(convertToQueryString(value, newKey));
      } else {
        queryStringArray.push(`${newKey}=${encodeURIComponent(value)}`);
      }
    }
  }

  return queryStringArray.join('&');
}

export function formatCurrency(amount: number, currency: string = 'USD', locale: string = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
}

export function toTitleCase(input: string): string {
  return input
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function formatTimestamp(timestamp: number) {
  let date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  let year = date.getFullYear();
  let month = String(date.getMonth() + 1).padStart(2, '0');
  let day = String(date.getDate()).padStart(2, '0');
  let hours = String(date.getHours()).padStart(2, '0');
  let minutes = String(date.getMinutes()).padStart(2, '0');
  let seconds = String(date.getSeconds()).padStart(2, '0');
  let milliseconds = String(date.getMilliseconds()).padStart(6, '0'); // Ensure 6 digits

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
}

export function formatTimestampUTC(timestamp: number) {
  let date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  let year = date.getUTCFullYear();
  let month = String(date.getUTCMonth() + 1).padStart(2, '0');
  let day = String(date.getUTCDate()).padStart(2, '0');
  let hours = String(date.getUTCHours()).padStart(2, '0');
  let minutes = String(date.getUTCMinutes()).padStart(2, '0');
  let seconds = String(date.getUTCSeconds()).padStart(2, '0');
  let milliseconds = String(date.getUTCMilliseconds()).padStart(6, '0'); // Ensure 6 digits

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
}

export function getOrdinal(num: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = num % 100;
  if (v >= 11 && v <= 13) return num + "th";
  switch (num % 10) {
    case 1: return num + "st";
    case 2: return num + "nd";
    case 3: return num + "rd";
    default: return num + "th";
  }
}

export function getOrdinalString(num: number): string {
  switch (num) {
    case 1: return 'first';
    case 2: return 'second';
    case 3: return 'third';
    case 4: return 'fourth';
    case 5: return 'fifth';
    default: return num + 'th';
  }
}

/**
 * Parse description text and convert link markup to HTML
 * Format: [text|linkId] where linkId is the ID from the links array
 * Example: [Privacy Policy|privacyPolicy] or [Terms & Conditions|termsOfUse]
 * Also supports direct URLs: [text](url)
 * 
 * @param description The description text containing link markup
 * @param environmentService The EnvironmentService instance to resolve link IDs
 * @param sanitizer The DomSanitizer instance to create SafeHtml
 * @returns SafeHtml with parsed links
 */
export function parseDescriptionWithLinks(
  description: string,
  environmentService: EnvironmentService,
  sanitizer: DomSanitizer
): SafeHtml {
  if (!description) return sanitizer.bypassSecurityTrustHtml('');
  
  // Pattern to match [text|linkId] or [text](url)
  // This regex matches: [text|linkId] (pipe inside brackets) or [text](url) (parentheses after brackets)
  // Use two separate patterns to handle both formats
  const linkPattern1 = /\[([^\]]+)\|([^\]]+)\]/g; // [text|linkId]
  const linkPattern2 = /\[([^\]]+)\]\(([^)]+)\)/g; // [text](url)
  
  const linkPlaceholders: { placeholder: string; html: string }[] = [];
  let placeholderIndex = 0;
  
  // First pass: find all [text|linkId] links and replace with placeholders
  let parsed = description.replace(linkPattern1, (fullMatch, linkText, linkId) => {
    const url = environmentService.getLinkUrlById(linkId);
    const link = environmentService.getLinkById(linkId);
    const target = link.target || '_blank';
    
    if (url) {
      // Escape HTML in link text to prevent XSS
      const escapedText = linkText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      
      // Escape URL to prevent XSS
      const escapedUrl = url
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      
      const placeholder = `__LINK_PLACEHOLDER_${placeholderIndex++}__`;
      const linkHtml = `<a href="${escapedUrl}" target="${target}" rel="noopener">${escapedText}</a>`;
      linkPlaceholders.push({ placeholder, html: linkHtml });
      return placeholder;
    }
    
    return fullMatch; // Return original if URL not found
  });
  
  // Second pass: find all [text](url) links and replace with placeholders
  parsed = parsed.replace(linkPattern2, (fullMatch, linkText, directUrl) => {
    if (directUrl) {
      // Escape HTML in link text to prevent XSS
      const escapedText = linkText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      
      // Escape URL to prevent XSS
      const escapedUrl = directUrl
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      
      const placeholder = `__LINK_PLACEHOLDER_${placeholderIndex++}__`;
      const linkHtml = `<a href="${escapedUrl}" target="_blank" rel="noopener">${escapedText}</a>`;
      linkPlaceholders.push({ placeholder, html: linkHtml });
      return placeholder;
    }
    
    return fullMatch; // Return original if URL not found
  });
  
  // Escape all HTML in the remaining text
  parsed = parsed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Replace placeholders with actual link HTML
  linkPlaceholders.forEach(({ placeholder, html }) => {
    parsed = parsed.replace(placeholder, html);
  });
  
  // Return as SafeHtml
  return sanitizer.bypassSecurityTrustHtml(parsed);
}